<template>
  <div>
    <SinoaWelcomeContent :typed-sections="typedSections" />
    <SinoaPSAContent
      v-if="state.showPSA"
      :typed-sections="typedSections"
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { buildSinoaContent, usePSAVisibility } from './data/helpers';
import SinoaWelcomeContent from './SinoaWelcomeContent.vue';
import SinoaPSAContent from './SinoaPSAContent.vue';
import { COLORS } from '@/helpers/colors';

const TYPED_SECTIONS_KEYS = {
  welcome1: '',
  welcome2: '',
  welcome3: '',
  bulletWelcome1: '',
  bulletWelcome2: '',
  psaIntro1: '',
  psaIntro2: '',
  psaBullet1: '',
  psaBullet2: '',
  psaBullet3: '',
  psaBullet4: '',
  psaToolsIntro1: '',
  psaToolsIntro2: '',
  psaTool1: '',
  psaTool2: '',
  psaTool3: '',
  psaTool4: '',
  psaTip: '',
  psaExample: '',
  psaFeedback: '',
};

const ANIMATION_SPEED = 1;
const SCROLL_INTERVAL = 50;

export default {
  name: 'SinoaWelcome',

  components: {
    SinoaWelcomeContent,
    SinoaPSAContent,
  },

  data() {
    return {
      COLORS,
      state: {
        showPSA: false,
        psaMarkedAsShown: false,
        hasTypewriterRun: false,
      },
      typedSections: { ...TYPED_SECTIONS_KEYS },
      currentStreamInterval: null,
      scrollInterval: null,
    };
  },

  computed: {
    ...mapState('user', ['firstName']),

    userName() {
      return this.firstName || 'User';
    },

    allContent() {
      return buildSinoaContent(this.userName, COLORS.grainfoxAIButton, this.$isMobile);
    },
  },

  watch: {
    'state.showPSA': {
      handler(newVal) {
        if (newVal && !this.state.hasTypewriterRun) {
          this.$nextTick(() => {
            this.animate();
          });
        } else if (!newVal) {
          this.stop();
          this.setContent();
        }
      },
      immediate: false,
    },
  },

  mounted() {
    this.loadPSAVisibility();
    if (!this.state.showPSA) {
      this.setContent();
    }
  },

  updated() {
    if (this.state.showPSA && !this.state.psaMarkedAsShown) {
      this.markPSAAsShown();
    }
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    ...mapMutations('chatbot', ['setTypewriterCompleted']),

    loadPSAVisibility() {
      const { shouldShowPSA } = usePSAVisibility();
      this.state.showPSA = shouldShowPSA();
    },

    markPSAAsShown() {
      const today = new Date().toISOString().split('T')[0];
      localStorage.setItem('sinoaPSAShownDate', today);
      this.state.psaMarkedAsShown = true;
    },

    setContent() {
      Object.keys(this.allContent).forEach((key) => {
        this.typedSections[key] = this.allContent[key];
      });
      this.state.hasTypewriterRun = false;
    },

    getScrollContainer() {
      let parent = this.$el?.parentElement;
      while (parent) {
        if (parent.classList?.contains('chat-container')) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    },

    startAutoScroll() {
      this.stopAutoScroll();
      this.scrollInterval = setInterval(() => {
        const container = this.getScrollContainer();
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, SCROLL_INTERVAL);
    },

    stopAutoScroll() {
      if (this.scrollInterval) {
        clearInterval(this.scrollInterval);
        this.scrollInterval = null;
      }
    },

    stopStreamInterval() {
      if (this.currentStreamInterval) {
        clearInterval(this.currentStreamInterval);
        this.currentStreamInterval = null;
      }
    },

    cleanup() {
      this.stopStreamInterval();
      this.stopAutoScroll();
    },

    resetTypedSections() {
      Object.keys(this.typedSections).forEach((key) => {
        this.typedSections[key] = '';
      });
    },

    typeCharacter(sectionKey, fullText, currentIndex) {
      if (currentIndex < fullText.length) {
        this.typedSections[sectionKey] = fullText.substring(0, currentIndex + 1);
        return true;
      }
      return false;
    },

    animateSection(sectionKey, fullText, onComplete) {
      let currentIndex = 0;

      const streamInterval = setInterval(() => {
        if (this.typeCharacter(sectionKey, fullText, currentIndex)) {
          currentIndex += 1;
        } else {
          clearInterval(streamInterval);
          onComplete();
        }
      }, ANIMATION_SPEED);

      this.currentStreamInterval = streamInterval;
    },

    typeNextSection(sections, currentSectionIndex = 0) {
      if (currentSectionIndex >= sections.length) {
        this.setTypewriterCompleted(true);
        this.stopAutoScroll();
        return;
      }

      const sectionKey = sections[currentSectionIndex];
      const fullText = this.allContent[sectionKey];

      this.animateSection(sectionKey, fullText, () => {
        this.typeNextSection(sections, currentSectionIndex + 1);
      });
    },

    animate() {
      this.cleanup();
      this.state.hasTypewriterRun = true;
      this.resetTypedSections();

      const sections = Object.keys(this.allContent);
      this.startAutoScroll();
      this.typeNextSection(sections);
    },

    stop() {
      this.stopStreamInterval();
    },
  },
};
</script>
